import type { Customer } from "@/server/platform/db/schema/crm/customer";
import type { Printer } from "@/server/platform/db/schema/pos/printer";
import type { Order } from "@/server/platform/db/schema/pos-sales/orders";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type {
  CheckoutAndCompleteRequest,
  CompleteOrderRequest,
  CreateOrderRequest,
  SyncAdjustmentsRequest,
  SyncAdjustmentsResponse,
  SyncOrdersRequest,
  SyncOrdersResponse,
} from "../../domain/contracts/offline-sync";
import type { OrderWithDetails } from "../../domain/repo/create-order";
import type { BranchUser } from "../../domain/repo/get-branch-users";
import type {
  KitchenTicketFilters,
  KitchenTicketPagination,
} from "../../domain/repo/get-kitchen-tickets";
import type {
  OrderFilters,
  OrderPagination,
  OrderSort,
} from "../../domain/repo/get-orders";
import type { PaymentMethod } from "../../domain/repo/get-payment-methods";
import type {
  GetStockAlertsOptions,
  StockAlertWithDetails,
} from "../../domain/repo/get-stock-alerts";
import type { MoveOrderItemsResult } from "../../domain/repo/move-order-items";
import type {
  BranchDevice,
  BranchProductWithDetails,
  BranchTableWithStatus,
  Category,
  Delivery,
  DeliveryPartner,
  GroupedKitchenTicket,
  KitchenStation,
  KitchenTicketStatus,
  KitchenTicketWithDetails,
  OrderWithSummary,
  PaymentWithMethod,
  Refund,
  RefundInput,
  UserBranch,
} from "../../domain/types";
import type { CartItem } from "../context/CartContext";

function createIdempotencyKey(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
}

export type SyncInboxStatus =
  | "pending"
  | "processing"
  | "completed"
  | "failed"
  | "dead"
  | "resolved";

export type SyncInboxListItem = {
  id: string;
  jobId: string;
  branchId: string;
  deviceId: string;
  shiftId: string;
  clientOrderId: string;
  idempotencyKey: string;
  status: SyncInboxStatus;
  serverOrderId: string | null;
  errorCode: string | null;
  errorMessage: string | null;
  retryCount: number;
  nextRetryAt: string | Date | null;
  createdAt: string | Date;
  processedAt: string | Date | null;
  updatedAt: string | Date;
  replayResult?: {
    serverCustomerId?: string;
    couponReplay?: Array<{
      code: string;
      applied: boolean;
      discountAmount?: number;
      errorCode?: string;
      error?: string;
    }>;
  } | null;
};

export type SyncQueueOverview = {
  scope: { branchId: string; deviceId?: string; shiftId?: string };
  countsByStatus: Array<{ status: SyncInboxStatus; value: number }>;
  byDevice: Array<{ deviceId: string; status: SyncInboxStatus; value: number }>;
  byShift: Array<{ shiftId: string; status: SyncInboxStatus; value: number }>;
  oldestBlocking: {
    id: string;
    clientOrderId: string;
    status: SyncInboxStatus;
    createdAt: string | Date;
    updatedAt: string | Date;
  } | null;
  blockingStatuses: SyncInboxStatus[];
};

export const posApi = {
  async getUserBranches(): Promise<UserBranch[]> {
    const response = await fetcher.get<{ data: UserBranch[] }>(
      `${config.apiUrl}/pos/branches`,
    );
    return response.data;
  },

  async getBranchDevices(branchId: string): Promise<BranchDevice[]> {
    const response = await fetcher.get<{ data: BranchDevice[] }>(
      `${config.apiUrl}/pos/branches/${branchId}/devices`,
    );
    return response.data;
  },

  async getBranchUsers(branchId: string): Promise<BranchUser[]> {
    const response = await fetcher.get<{ data: BranchUser[] }>(
      `${config.apiUrl}/pos/branches/${branchId}/users`,
    );
    return response.data;
  },

  async getDeliveryPartners(branchId: string): Promise<DeliveryPartner[]> {
    const response = await fetcher.get<{ data: DeliveryPartner[] }>(
      `${config.apiUrl}/pos/branches/${branchId}/delivery-partners`,
    );
    return response.data;
  },

  async getBranchPrinters(branchId: string): Promise<Printer[]> {
    const response = await fetcher.get<{ data: Printer[] }>(
      `${config.apiUrl}/pos/branches/${branchId}/printers`,
    );
    return response.data;
  },

  async getBranchTables(branchId: string): Promise<BranchTableWithStatus[]> {
    const response = await fetcher.get<{ data: BranchTableWithStatus[] }>(
      `${config.apiUrl}/pos/branches/${branchId}/tables`,
    );
    return response.data;
  },

  async getBranchReceiptInfo(branchId: string): Promise<{
    branch: {
      name: string;
      addressLine1: string | null;
      addressLine2: string | null;
      district: string | null;
      province: string | null;
      postalCode: string | null;
    };
    billSettings: {
      logoUrl: string | null;
      brandName: string | null;
      phone: string | null;
      footerThankYouLao: string | null;
      footerThankYouEnglish: string | null;
      showTaxOnBill: boolean;
    };
  }> {
    const response = await fetcher.get<{
      data: {
        branch: {
          name: string;
          addressLine1: string | null;
          addressLine2: string | null;
          district: string | null;
          province: string | null;
          postalCode: string | null;
        };
        billSettings: {
          logoUrl: string | null;
          brandName: string | null;
          phone: string | null;
          footerThankYouLao: string | null;
          footerThankYouEnglish: string | null;
          showTaxOnBill: boolean;
        };
      };
    }>(`${config.apiUrl}/pos/branches/${branchId}/receipt-info`);
    return response.data;
  },

  async getBranchBillSettings(branchId: string): Promise<{
    logoUrl: string | null;
    brandName: string | null;
    phone: string | null;
    footerThankYouLao: string | null;
    footerThankYouEnglish: string | null;
    showTaxOnBill: boolean;
  }> {
    const response = await fetcher.get<{
      data: {
        logoUrl: string | null;
        brandName: string | null;
        phone: string | null;
        footerThankYouLao: string | null;
        footerThankYouEnglish: string | null;
        showTaxOnBill: boolean;
      };
    }>(`${config.apiUrl}/pos/branches/${branchId}/bill-settings`);
    return response.data;
  },

  async updateBranchBillSettings(
    branchId: string,
    data: {
      logoUrl?: string | null;
      brandName?: string | null;
      phone?: string | null;
      footerThankYouLao?: string | null;
      footerThankYouEnglish?: string | null;
      showTaxOnBill?: boolean | null;
    },
  ): Promise<{
    logoUrl: string | null;
    brandName: string | null;
    phone: string | null;
    footerThankYouLao: string | null;
    footerThankYouEnglish: string | null;
    showTaxOnBill: boolean;
  }> {
    const response = await fetcher.patch<{
      data: {
        logoUrl: string | null;
        brandName: string | null;
        phone: string | null;
        footerThankYouLao: string | null;
        footerThankYouEnglish: string | null;
        showTaxOnBill: boolean;
      };
    }>(`${config.apiUrl}/pos/branches/${branchId}/bill-settings`, data);
    return response.data;
  },

  async getBranchCustomerDisplaySettings(branchId: string): Promise<{
    paymentQrUrls: string[];
    socialMediaUrls?: {
      whatsapp?: string | null;
      facebook?: string | null;
      instagram?: string | null;
      tiktok?: string | null;
    };
  }> {
    const response = await fetcher.get<{
      data: {
        paymentQrUrls: string[];
        socialMediaUrls?: {
          whatsapp?: string | null;
          facebook?: string | null;
          instagram?: string | null;
          tiktok?: string | null;
        };
      };
    }>(`${config.apiUrl}/pos/branches/${branchId}/customer-display-settings`);
    return response.data;
  },

  async updateBranchCustomerDisplaySettings(
    branchId: string,
    data: {
      paymentQrUrls: string[];
      socialMediaUrls?: {
        whatsapp?: string | null;
        facebook?: string | null;
        instagram?: string | null;
        tiktok?: string | null;
      };
    },
  ): Promise<{
    paymentQrUrls: string[];
    socialMediaUrls?: {
      whatsapp?: string | null;
      facebook?: string | null;
      instagram?: string | null;
      tiktok?: string | null;
    };
  }> {
    const response = await fetcher.patch<{
      data: {
        paymentQrUrls: string[];
        socialMediaUrls?: {
          whatsapp?: string | null;
          facebook?: string | null;
          instagram?: string | null;
          tiktok?: string | null;
        };
      };
    }>(
      `${config.apiUrl}/pos/branches/${branchId}/customer-display-settings`,
      data,
    );
    return response.data;
  },

  async getCategories(branchId: string): Promise<Category[]> {
    const response = await fetcher.get<{ data: Category[] }>(
      `${config.apiUrl}/pos/branches/${branchId}/categories`,
    );
    return response.data;
  },

  async getBranchProducts(
    branchId: string,
    categoryId?: string | null,
    limit = 20,
    offset = 0,
  ): Promise<{
    data: BranchProductWithDetails[];
    total: number;
  }> {
    const url = new URL(`${config.apiUrl}/pos/branches/${branchId}/products`);
    if (categoryId) {
      url.searchParams.set("categoryId", categoryId);
    }
    url.searchParams.set("limit", String(limit));
    url.searchParams.set("offset", String(offset));
    const response = await fetcher.get<{
      data: BranchProductWithDetails[];
      total: number;
    }>(url.toString());
    return response;
  },

  async searchProducts(
    branchId: string,
    query: string,
  ): Promise<BranchProductWithDetails[]> {
    const url = new URL(
      `${config.apiUrl}/pos/branches/${branchId}/products/search`,
    );
    url.searchParams.set("q", query);
    const response = await fetcher.get<{ data: BranchProductWithDetails[] }>(
      url.toString(),
    );
    return response.data;
  },

  async getProductVariants(productId: string, branchId: string) {
    const response = await fetcher.get<{
      data: Array<{
        id: string;
        name: string;
        sku: string | null;
        price: number | null;
        sortOrder: number;
        optionValueIds: string[];
      }>;
    }>(
      `${config.apiUrl}/pos/branches/${branchId}/products/${productId}/variants`,
    );
    return response.data;
  },

  async getProductOptions(productId: string, branchId: string) {
    const response = await fetcher.get<{
      data: Array<{
        id: string;
        name: string;
        values: Array<{
          id: string;
          value: string;
        }>;
      }>;
    }>(
      `${config.apiUrl}/pos/branches/${branchId}/products/${productId}/options`,
    );
    return response.data;
  },

  async getVariantModifiers(variantId: string, branchId: string) {
    const response = await fetcher.get<{
      data: Array<{
        id: string;
        modifierId: string;
        priceAdjustment: number;
        required: boolean;
        modifier: {
          id: string;
          name: string;
          type: "single" | "multiple";
          options: Array<{
            id: string;
            value: string;
            label: string;
            isDefault: boolean;
            price: number;
          }>;
        };
      }>;
    }>(
      `${config.apiUrl}/pos/branches/${branchId}/variants/${variantId}/modifiers`,
    );
    return response.data;
  },

  async findVariantByOptions(
    productId: string,
    branchId: string,
    optionValueIds: string[],
  ) {
    const response = await fetcher.post<{
      data: {
        id: string;
        name: string;
        price: number | null;
      };
    }>(
      `${config.apiUrl}/pos/branches/${branchId}/products/${productId}/find-variant`,
      {
        optionValueIds,
      },
    );
    return response.data;
  },

  async createOrder(
    cartItems: CreateOrderRequest["cartItems"],
    selection: CreateOrderRequest["selection"],
    isDelivery: CreateOrderRequest["isDelivery"],
    deliveryData?: CreateOrderRequest["deliveryData"],
    customerId?: CreateOrderRequest["customerId"],
    couponCode?: CreateOrderRequest["couponCode"],
    couponApplyToCartIndices?: CreateOrderRequest["couponApplyToCartIndices"],
    couponApplications?: CreateOrderRequest["couponApplications"],
  ): Promise<OrderWithDetails> {
    const response = await fetcher.post<{ data: OrderWithDetails }>(
      `${config.apiUrl}/pos/orders`,
      {
        cartItems,
        selection,
        isDelivery,
        deliveryData,
        customerId,
        couponCode,
        couponApplyToCartIndices,
        couponApplications,
      },
    );
    return response.data;
  },

  /** ຈ່າຍທັນທີ: ສ້າງ order + ຈ່າຍໃນ transaction ດຽວ */
  async createOrderAndComplete(
    body: CheckoutAndCompleteRequest,
  ): Promise<Order> {
    const response = await fetcher.post<{ data: Order }>(
      `${config.apiUrl}/pos/orders/checkout-and-complete`,
      body,
      {
        headers: { "Idempotency-Key": createIdempotencyKey() },
      },
    );
    return response.data;
  },

  async getOrders(
    branchId: string,
    filters: OrderFilters = {},
    pagination: OrderPagination = { limit: 20, offset: 0 },
    sort: OrderSort = { field: "createdAt", order: "desc" },
  ): Promise<{ data: OrderWithSummary[]; total: number }> {
    const url = new URL(`${config.apiUrl}/pos/orders`);
    url.searchParams.set("branchId", branchId);

    if (filters.status && filters.status.length > 0) {
      url.searchParams.set("status", filters.status.join(","));
    }
    if (filters.orderType && filters.orderType.length > 0) {
      url.searchParams.set("orderType", filters.orderType.join(","));
    }
    if (filters.startDate) {
      url.searchParams.set("startDate", filters.startDate);
    }
    if (filters.endDate) {
      url.searchParams.set("endDate", filters.endDate);
    }
    if (filters.search) {
      url.searchParams.set("search", filters.search);
    }
    if (filters.tableId) {
      url.searchParams.set("tableId", filters.tableId);
    }
    if (filters.shiftId) {
      url.searchParams.set("shiftId", filters.shiftId);
    }
    if (filters.paymentMethodId?.trim()) {
      url.searchParams.set("paymentMethodId", filters.paymentMethodId.trim());
    }
    url.searchParams.set("limit", String(pagination.limit));
    url.searchParams.set("offset", String(pagination.offset));
    url.searchParams.set("sortField", sort.field);
    url.searchParams.set("sortOrder", sort.order);

    const response = await fetcher.get<{
      data: OrderWithSummary[];
      total: number;
    }>(url.toString());
    return response;
  },

  async getCarryOverOpenOrders(
    branchId: string,
    currentShiftId: string,
    filters: import("../../domain/repo/get-orders").CarryOverOpenOrderFilters = {},
  ): Promise<{ data: OrderWithSummary[] }> {
    const url = new URL(`${config.apiUrl}/pos/orders/carry-over-open`);
    url.searchParams.set("branchId", branchId);
    url.searchParams.set("currentShiftId", currentShiftId);
    if (filters.orderType && filters.orderType.length > 0) {
      url.searchParams.set("orderType", filters.orderType.join(","));
    }
    if (filters.search) {
      url.searchParams.set("search", filters.search);
    }
    return fetcher.get<{ data: OrderWithSummary[] }>(url.toString());
  },

  async getOrderDetail(
    orderId: string,
    branchId: string,
  ): Promise<
    OrderWithDetails & {
      tableName?: string | null;
      salespersonName?: string | null;
      delivery?: Delivery | null;
      customer?: Customer | null;
      memberReceipt?: {
        phone: string | null;
        firstName?: string | null;
        lastName?: string | null;
        loyaltyPointsBalance: number;
        loyaltyPointsEarned?: number;
        balanceIncludesEarned?: boolean;
      } | null;
    }
  > {
    const url = new URL(`${config.apiUrl}/pos/orders/${orderId}`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{
      data: OrderWithDetails & {
        tableName?: string | null;
        salespersonName?: string | null;
      };
    }>(url.toString());
    return response.data;
  },

  async updateOrderTable(
    orderId: string,
    branchId: string,
    tableId: string | null,
  ): Promise<Order> {
    const url = new URL(`${config.apiUrl}/pos/orders/${orderId}/table`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.patch<{ data: Order }>(url.toString(), {
      tableId,
    });
    return response.data;
  },

  async moveOrderItems(
    sourceOrderId: string,
    branchId: string,
    targetOrderId: string,
  ): Promise<MoveOrderItemsResult> {
    const url = new URL(
      `${config.apiUrl}/pos/orders/${sourceOrderId}/move-items`,
    );
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.post<{ data: MoveOrderItemsResult }>(
      url.toString(),
      {
        targetOrderId,
      },
    );
    return response.data;
  },

  async voidOrderItem(
    orderId: string,
    branchId: string,
    orderItemId: string,
    reason: string,
  ): Promise<{ order: Order }> {
    const url = new URL(
      `${config.apiUrl}/pos/orders/${orderId}/items/${orderItemId}/void`,
    );
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.post<{ data: { order: Order } }>(
      url.toString(),
      { reason: reason.trim() },
    );
    return response.data;
  },

  async addOrderItems(
    orderId: string,
    branchId: string,
    cartItems: CartItem[],
  ): Promise<OrderWithDetails> {
    const url = new URL(`${config.apiUrl}/pos/orders/${orderId}/items`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.post<{ data: OrderWithDetails }>(
      url.toString(),
      { cartItems },
    );
    return response.data;
  },

  async getKitchenTickets(
    branchId: string,
    filters: KitchenTicketFilters = {},
    pagination: KitchenTicketPagination = { limit: 50, offset: 0 },
  ): Promise<{
    data: KitchenTicketWithDetails[];
    total: number;
    grouped?: GroupedKitchenTicket[];
  }> {
    const url = new URL(`${config.apiUrl}/pos/kitchen/tickets`);
    url.searchParams.set("branchId", branchId);

    if (filters.orderId) {
      url.searchParams.set("orderId", filters.orderId);
    }
    if (filters.stationId) {
      url.searchParams.set("stationId", filters.stationId);
    }
    if (filters.status) {
      url.searchParams.set("status", filters.status);
    }
    if (filters.shiftId) {
      url.searchParams.set("shiftId", filters.shiftId);
    }
    if (filters.groupBy && filters.groupBy.length > 0) {
      url.searchParams.set("groupBy", filters.groupBy.join(","));
    }

    url.searchParams.set("limit", String(pagination.limit));
    url.searchParams.set("offset", String(pagination.offset));

    const response = await fetcher.get<{
      data: KitchenTicketWithDetails[];
      total: number;
      grouped?: GroupedKitchenTicket[];
    }>(url.toString());
    return response;
  },

  async updateKitchenTicketStatus(
    ticketId: string,
    status: "IN_PROGRESS" | "DONE",
    assigneeId?: string,
  ): Promise<{ data: { id: string; status: KitchenTicketStatus } }> {
    const response = await fetcher.patch<{
      data: { id: string; status: KitchenTicketStatus };
    }>(`${config.apiUrl}/pos/kitchen/tickets/${ticketId}/status`, {
      status,
      assigneeId,
    });
    return response;
  },

  async getKitchenStations(branchId: string): Promise<KitchenStation[]> {
    const url = new URL(`${config.apiUrl}/pos/kitchen/stations`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{ data: KitchenStation[] }>(
      url.toString(),
    );
    return response.data;
  },

  async getKitchenRouting(branchId: string): Promise<
    Array<{
      variantId: string;
      stationId: string;
      stationName: string;
      stationCode: string | null;
    }>
  > {
    const url = new URL(`${config.apiUrl}/pos/kitchen/routing`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{
      data: Array<{
        variantId: string;
        stationId: string;
        stationName: string;
        stationCode: string | null;
      }>;
    }>(url.toString());
    return response.data;
  },

  async getPaymentMethods(branchId: string): Promise<PaymentMethod[]> {
    const url = new URL(`${config.apiUrl}/pos/payment-methods`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{ data: PaymentMethod[] }>(
      url.toString(),
    );
    return response.data;
  },

  async validateOrderCompletion(
    orderId: string,
    branchId: string,
  ): Promise<{ valid: boolean; errors: string[] }> {
    const url = new URL(
      `${config.apiUrl}/pos/orders/${orderId}/validate-completion`,
    );
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{ valid: boolean; errors: string[] }>(
      url.toString(),
    );
    return response;
  },

  async completeOrder(
    orderId: CompleteOrderRequest["orderId"],
    branchId: CompleteOrderRequest["branchId"],
    payments: CompleteOrderRequest["payments"],
    skipKitchenValidation = false,
    voucherCodes?: CompleteOrderRequest["voucherCodes"],
    couponCode?: CompleteOrderRequest["couponCode"],
    couponOrderItemIds?: CompleteOrderRequest["couponOrderItemIds"],
    couponApplications?: CompleteOrderRequest["couponApplications"],
    customerId?: CompleteOrderRequest["customerId"],
    rewardId?: CompleteOrderRequest["rewardId"],
    deviceId?: CompleteOrderRequest["deviceId"],
  ): Promise<Order> {
    const url = new URL(`${config.apiUrl}/pos/orders/${orderId}/complete`);
    url.searchParams.set("branchId", branchId);
    if (deviceId) {
      url.searchParams.set("deviceId", deviceId);
    }
    const response = await fetcher.post<{ data: Order }>(
      url.toString(),
      {
        payments,
        skipKitchenValidation,
        voucherCodes,
        couponCode,
        couponOrderItemIds,
        couponApplications,
        customerId,
        rewardId,
      },
      {
        headers: { "Idempotency-Key": createIdempotencyKey() },
        timeoutMs: 120_000,
      },
    );
    return response.data;
  },

  async syncOfflineOrders(
    body: SyncOrdersRequest,
  ): Promise<SyncOrdersResponse> {
    return await fetcher.post<SyncOrdersResponse>(
      `${config.apiUrl}/pos/sync/orders`,
      body,
    );
  },

  async syncOfflineAdjustments(
    body: SyncAdjustmentsRequest,
  ): Promise<SyncAdjustmentsResponse> {
    return await fetcher.post<SyncAdjustmentsResponse>(
      `${config.apiUrl}/pos/sync/adjustments`,
      body,
    );
  },

  async getSyncJobStatus(jobId: string): Promise<{
    id: string;
    branchId: string;
    deviceId: string;
    shiftId: string;
    status: string;
    totalOrders: number;
    processedOrders: number;
    failedOrders: number;
    orders: Array<{
      id: string;
      clientOrderId: string;
      status: string;
      serverOrderId: string | null;
      errorCode: string | null;
      errorMessage: string | null;
      retryCount: number;
      replayResult?: {
        serverCustomerId?: string;
        couponReplay?: Array<{
          code: string;
          applied: boolean;
          discountAmount?: number;
          errorCode?: string;
          error?: string;
        }>;
      } | null;
    }>;
  }> {
    const response = await fetcher.get<{
      data: {
        id: string;
        branchId: string;
        deviceId: string;
        shiftId: string;
        status: string;
        totalOrders: number;
        processedOrders: number;
        failedOrders: number;
        orders: Array<{
          id: string;
          clientOrderId: string;
          status: string;
          serverOrderId: string | null;
          errorCode: string | null;
          errorMessage: string | null;
          retryCount: number;
        }>;
      };
    }>(`${config.apiUrl}/pos/sync/jobs/${jobId}`);
    return response.data;
  },

  async getPendingSyncCountForShift(
    shiftId: string,
    branchId: string,
  ): Promise<number> {
    const url = new URL(`${config.apiUrl}/pos/sync/shifts/${shiftId}/pending`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{
      data: { shiftId: string; pending: number };
    }>(url.toString());
    return response.data.pending;
  },

  async getSyncQueueOverview(params: {
    branchId: string;
    deviceId?: string;
    shiftId?: string;
  }): Promise<SyncQueueOverview> {
    const url = new URL(`${config.apiUrl}/pos/sync/queue`);
    url.searchParams.set("branchId", params.branchId);
    if (params.deviceId) url.searchParams.set("deviceId", params.deviceId);
    if (params.shiftId) url.searchParams.set("shiftId", params.shiftId);
    const response = await fetcher.get<{ data: SyncQueueOverview }>(
      url.toString(),
    );
    return response.data;
  },

  async listSyncInboxOrders(params: {
    branchId: string;
    deviceId?: string;
    shiftId?: string;
    status?: SyncInboxStatus;
    limit?: number;
    offset?: number;
  }): Promise<{
    items: SyncInboxListItem[];
    total: number;
    limit: number;
    offset: number;
  }> {
    const url = new URL(`${config.apiUrl}/pos/sync/inbox`);
    url.searchParams.set("branchId", params.branchId);
    if (params.deviceId) url.searchParams.set("deviceId", params.deviceId);
    if (params.shiftId) url.searchParams.set("shiftId", params.shiftId);
    if (params.status) url.searchParams.set("status", params.status);
    if (params.limit) url.searchParams.set("limit", String(params.limit));
    if (params.offset) url.searchParams.set("offset", String(params.offset));
    const response = await fetcher.get<{
      data: {
        items: SyncInboxListItem[];
        total: number;
        limit: number;
        offset: number;
      };
    }>(url.toString());
    return response.data;
  },

  async retrySyncInboxOrder(inboxId: string): Promise<SyncInboxListItem> {
    const response = await fetcher.post<{ data: SyncInboxListItem }>(
      `${config.apiUrl}/pos/sync/inbox/${inboxId}/retry`,
    );
    return response.data;
  },

  async resolveSyncInboxOrder(
    inboxId: string,
    reason: string,
  ): Promise<SyncInboxListItem> {
    const response = await fetcher.post<{ data: SyncInboxListItem }>(
      `${config.apiUrl}/pos/sync/inbox/${inboxId}/resolve`,
      { reason },
    );
    return response.data;
  },

  async voidOrder(
    orderId: string,
    branchId: string,
    reason?: string,
  ): Promise<Order> {
    const url = new URL(`${config.apiUrl}/pos/orders/${orderId}/void`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.post<{ data: Order }>(url.toString(), {
      reason,
    });
    return response.data;
  },

  async getOrderPayments(
    orderId: string,
    branchId: string,
  ): Promise<PaymentWithMethod[]> {
    const url = new URL(`${config.apiUrl}/pos/orders/${orderId}/payments`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{ data: PaymentWithMethod[] }>(
      url.toString(),
    );
    return response.data;
  },

  async createRefund(
    orderId: string,
    branchId: string,
    refundInput: RefundInput,
  ): Promise<Refund[]> {
    const url = new URL(`${config.apiUrl}/pos/orders/${orderId}/refund`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.post<{ data: Refund[] }>(url.toString(), {
      paymentIds: refundInput.paymentIds,
      reasonCode: refundInput.reasonCode,
      reason: refundInput.reason,
      metadata: refundInput.metadata,
    });
    return response.data;
  },

  async approveRefund(refundId: string, branchId: string): Promise<Refund> {
    const url = new URL(`${config.apiUrl}/pos/refunds/${refundId}/approve`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.post<{ data: Refund }>(url.toString());
    return response.data;
  },

  async completeRefund(refundId: string, branchId: string): Promise<Refund> {
    const url = new URL(`${config.apiUrl}/pos/refunds/${refundId}/complete`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.post<{ data: Refund }>(url.toString());
    return response.data;
  },

  async rejectRefund(
    refundId: string,
    branchId: string,
    reason?: string,
  ): Promise<Refund> {
    const url = new URL(`${config.apiUrl}/pos/refunds/${refundId}/reject`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.post<{ data: Refund }>(url.toString(), {
      reason,
    });
    return response.data;
  },

  async getOrderRefunds(orderId: string, branchId: string): Promise<Refund[]> {
    const url = new URL(`${config.apiUrl}/pos/orders/${orderId}/refunds`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{ data: Refund[] }>(url.toString());
    return response.data;
  },

  async getOrderDelivery(
    orderId: string,
    branchId: string,
  ): Promise<import("../../domain/types").Delivery | null> {
    const url = new URL(`${config.apiUrl}/pos/orders/${orderId}/delivery`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{
      data: import("../../domain/types").Delivery | null;
    }>(url.toString());
    return response.data;
  },

  async getBranchTaxSettings(branchId: string): Promise<{
    taxRate: number;
    taxIncluded: boolean;
  }> {
    const response = await fetcher.get<{
      data: { taxRate: number; taxIncluded: boolean };
    }>(`${config.apiUrl}/pos/branches/${branchId}/tax-settings`);
    return response.data;
  },

  async getServiceChargePresets(branchId: string): Promise<
    Array<{
      id: string;
      name: string;
      calculationMode: string;
      rate: number | null;
      amount: number;
      metadata: unknown;
    }>
  > {
    const response = await fetcher.get<{
      data: Array<{
        id: string;
        name: string;
        calculationMode: string;
        rate: number | null;
        amount: number;
        metadata: unknown;
      }>;
    }>(`${config.apiUrl}/pos/branches/${branchId}/service-charge-presets`);
    return response.data;
  },

  async getShifts(
    branchId: string,
  ): Promise<import("../../domain/repo/get-shifts").Shift[]> {
    const url = new URL(`${config.apiUrl}/pos/shifts`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{
      data: import("../../domain/repo/get-shifts").Shift[];
    }>(url.toString());
    return response.data;
  },

  async lookupShifts(
    branchId: string,
    options: { limit?: number; skip?: number; search?: string } = {},
  ): Promise<{
    items: import("../../domain/repo/get-shifts").Shift[];
    total: number;
  }> {
    const url = new URL(`${config.apiUrl}/pos/shifts/lookup`);
    url.searchParams.set("branchId", branchId);
    if (options.limit != null)
      url.searchParams.set("limit", String(options.limit));
    if (options.skip != null)
      url.searchParams.set("skip", String(options.skip));
    if (options.search?.trim())
      url.searchParams.set("q", options.search.trim());
    const response = await fetcher.get<{
      items: import("../../domain/repo/get-shifts").Shift[];
      total: number;
    }>(url.toString());
    return { items: response.items ?? [], total: response.total ?? 0 };
  },

  async getLatestShift(
    branchId: string,
  ): Promise<import("../../domain/repo/get-shifts").Shift | null> {
    const url = new URL(`${config.apiUrl}/pos/shifts/latest`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{
      data: import("../../domain/repo/get-shifts").Shift | null;
    }>(url.toString());
    return response.data;
  },

  async getOpenShift(
    branchId: string,
    deviceId: string,
  ): Promise<import("../../domain/repo/get-open-shift").PosShift | null> {
    const url = new URL(`${config.apiUrl}/pos/shifts/open`);
    url.searchParams.set("branchId", branchId);
    url.searchParams.set("deviceId", deviceId);
    const response = await fetcher.get<{
      data: import("../../domain/repo/get-open-shift").PosShift | null;
    }>(url.toString());
    return response.data;
  },

  async openShift(
    branchId: string,
    deviceId: string,
    openingFloat: number,
  ): Promise<import("../../domain/repo/open-shift").PosShift> {
    const response = await fetcher.post<{
      data: import("../../domain/repo/open-shift").PosShift;
    }>(`${config.apiUrl}/pos/shifts/open`, {
      branchId,
      deviceId,
      openingFloat,
    });
    return response.data;
  },

  async calculateExpectedCash(
    shiftId: string,
    branchId: string,
  ): Promise<number> {
    const url = new URL(`${config.apiUrl}/pos/shifts/${shiftId}/expected-cash`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{ data: number }>(url.toString());
    return response.data;
  },

  async closeShift(
    shiftId: string,
    branchId: string,
    closingAmount: number,
    notes?: string,
  ): Promise<import("../../domain/repo/close-shift").PosShift> {
    const url = new URL(`${config.apiUrl}/pos/shifts/${shiftId}/close`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.post<{
      data: import("../../domain/repo/close-shift").PosShift;
    }>(url.toString(), {
      closingAmount,
      notes,
    });
    return response.data;
  },

  async getShiftReport(
    shiftId: string,
    branchId: string,
  ): Promise<import("../../domain/repo/get-shift-report").ShiftReport> {
    const url = new URL(`${config.apiUrl}/pos/shifts/${shiftId}/report`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{
      data: import("../../domain/repo/get-shift-report").ShiftReport;
    }>(url.toString());
    return response.data;
  },

  async getSalesByPaymentMethod(
    query: import("../../domain/contracts/reports").PosSalesByPaymentMethodQueryDTO,
  ): Promise<
    import("../../domain/contracts/reports").SalesByPaymentMethodResultDTO
  > {
    const url = new URL(
      `${config.apiUrl}/pos/reports/sales-by-payment-method`,
    );
    url.searchParams.set("branchId", query.branchId);
    url.searchParams.set("groupBy", query.groupBy ?? "method");
    url.searchParams.set("limit", String(query.limit ?? 100));
    url.searchParams.set("offset", String(query.offset ?? 0));

    const hasShift =
      (query.shiftIds
        ?.split(",")
        .map((s) => s.trim())
        .filter(Boolean).length ?? 0) > 0;
    if (hasShift && query.shiftIds) {
      url.searchParams.set("shiftIds", query.shiftIds);
    } else {
      if (query.dateFrom) url.searchParams.set("dateFrom", query.dateFrom);
      if (query.dateTo) url.searchParams.set("dateTo", query.dateTo);
    }
    if (query.methodIds) url.searchParams.set("methodIds", query.methodIds);
    if (query.methodTypes)
      url.searchParams.set("methodTypes", query.methodTypes);
    if (query.status) url.searchParams.set("status", query.status);
    if (query.sort?.length) {
      url.searchParams.set("sort", JSON.stringify(query.sort));
    }

    return fetcher.get(url.toString());
  },

  async recalculateShift(
    shiftId: string,
    branchId: string,
  ): Promise<import("../../domain/repo/recalculate-shift").PosShift> {
    const url = new URL(`${config.apiUrl}/pos/shifts/${shiftId}/recalculate`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.post<{
      data: import("../../domain/repo/recalculate-shift").PosShift;
    }>(url.toString());
    return response.data;
  },

  async getStockAlerts(
    branchId: string,
    options: GetStockAlertsOptions = {},
  ): Promise<StockAlertWithDetails[]> {
    const url = new URL(`${config.apiUrl}/pos/stock-alerts`);
    url.searchParams.set("branchId", branchId);
    if (options.includeAcknowledged) {
      url.searchParams.set("includeAcknowledged", "true");
    }
    if (options.limit) {
      url.searchParams.set("limit", String(options.limit));
    }
    if (options.offset) {
      url.searchParams.set("offset", String(options.offset));
    }
    const response = await fetcher.get<{ data: StockAlertWithDetails[] }>(
      url.toString(),
    );
    return response.data;
  },

  async getStockAlertsCount(branchId: string): Promise<{
    total: number;
    outOfStock: number;
    lowStock: number;
  }> {
    const url = new URL(`${config.apiUrl}/pos/stock-alerts/count`);
    url.searchParams.set("branchId", branchId);
    const response = await fetcher.get<{
      data: { total: number; outOfStock: number; lowStock: number };
    }>(url.toString());
    return response.data;
  },

  async acknowledgeStockAlert(alertId: string): Promise<void> {
    await fetcher.patch(
      `${config.apiUrl}/pos/stock-alerts/${alertId}/acknowledge`,
      {},
    );
  },

  async findOrCreateCustomerByPhone(phone: string): Promise<Customer> {
    const response = await fetcher.post<{
      customer: Customer;
    }>(`${config.apiUrl}/pos/customers/find-or-create`, { phone });
    return response.customer;
  },

  async getCustomerLoyaltyBalance(customerId: string): Promise<number> {
    const response = await fetcher.get<{ balance: number }>(
      `${config.apiUrl}/pos/customers/${encodeURIComponent(customerId)}/loyalty-balance`,
      undefined,
      { timeoutMs: 10_000 },
    );
    return response.balance;
  },

  async getEligibleCoupons(
    customerId?: string,
    orderType?: string,
    orderSubtotal?: number,
  ): Promise<{
    coupons: Array<
      typeof import("@/server/platform/db/schema/pos-sales/coupon").coupon.$inferSelect & {
        calculatedDiscount: number;
      }
    >;
    bestCoupon:
      | (typeof import("@/server/platform/db/schema/pos-sales/coupon").coupon.$inferSelect & {
          calculatedDiscount: number;
        })
      | null;
  }> {
    const url = new URL(`${config.apiUrl}/pos/coupons/eligible`);
    if (customerId) url.searchParams.set("customerId", customerId);
    if (orderType) url.searchParams.set("orderType", orderType);
    if (orderSubtotal !== undefined)
      url.searchParams.set("orderSubtotal", String(orderSubtotal));
    const response = await fetcher.get<{
      coupons: Array<
        typeof import("@/server/platform/db/schema/pos-sales/coupon").coupon.$inferSelect & {
          calculatedDiscount: number;
        }
      >;
      bestCoupon:
        | (typeof import("@/server/platform/db/schema/pos-sales/coupon").coupon.$inferSelect & {
            calculatedDiscount: number;
          })
        | null;
    }>(url.toString());
    return response;
  },

  /** ລາຍການ coupon ທີ່ໃຊ້ໄດ້ແບບແບ່ງໜ້າ (InfiniteCombobox) */
  async getEligibleCouponsPaged(params: {
    customerId?: string;
    orderType?: string;
    orderSubtotal?: number;
    search?: string;
    page: number;
    pageSize?: number;
  }): Promise<{
    coupons: Array<
      typeof import("@/server/platform/db/schema/pos-sales/coupon").coupon.$inferSelect & {
        calculatedDiscount: number;
      }
    >;
    nextPage: number | null;
  }> {
    const url = new URL(`${config.apiUrl}/pos/coupons/eligible`);
    if (params.customerId)
      url.searchParams.set("customerId", params.customerId);
    if (params.orderType) url.searchParams.set("orderType", params.orderType);
    if (params.orderSubtotal !== undefined) {
      url.searchParams.set("orderSubtotal", String(params.orderSubtotal));
    }
    url.searchParams.set("page", String(params.page));
    url.searchParams.set("pageSize", String(params.pageSize ?? 20));
    if (params.search?.trim()) {
      url.searchParams.set("search", params.search.trim());
    }
    const response = await fetcher.get<{
      coupons: Array<
        typeof import("@/server/platform/db/schema/pos-sales/coupon").coupon.$inferSelect & {
          calculatedDiscount: number;
        }
      >;
      nextPage: number | null;
    }>(url.toString());
    return response;
  },

  async validateCoupon(
    code: string,
    customerId?: string,
    orderSubtotal?: number,
    selectedSubtotal?: number,
    /** ยอดฐานรวมภาษีสำหรับคำนวณส่วนลด (ส่งแล้วจะใช้แทน orderSubtotal/selectedSubtotal) */
    baseAmount?: number,
  ): Promise<{
    valid: boolean;
    coupon?: typeof import("@/server/platform/db/schema/pos-sales/coupon").coupon.$inferSelect;
    calculatedDiscount?: number;
    error?: string;
  }> {
    const response = await fetcher.post<{
      valid: boolean;
      coupon?: typeof import("@/server/platform/db/schema/pos-sales/coupon").coupon.$inferSelect;
      calculatedDiscount?: number;
      error?: string;
    }>(`${config.apiUrl}/pos/coupons/validate`, {
      code,
      customerId,
      orderSubtotal,
      selectedSubtotal,
      baseAmount,
    });
    return response;
  },

  async validateVoucher(
    code: string,
    customerId?: string,
  ): Promise<{
    valid: boolean;
    voucher?: typeof import("@/server/platform/db/schema/crm/voucher").voucher.$inferSelect;
    error?: string;
  }> {
    const response = await fetcher.post<{
      valid: boolean;
      voucher?: typeof import("@/server/platform/db/schema/crm/voucher").voucher.$inferSelect;
      error?: string;
    }>(`${config.apiUrl}/pos/vouchers/validate`, {
      code,
      customerId,
    });
    return response;
  },

  async previewLoyaltyPoints(params: {
    customerId: string;
    items: Array<{
      variantId: string;
      quantity: number;
      lineSubtotal: number;
    }>;
  }): Promise<{
    points: number;
    ruleId: string | null;
    ruleName: string | null;
  }> {
    return fetcher.post<{
      points: number;
      ruleId: string | null;
      ruleName: string | null;
    }>(
      `${config.apiUrl}/pos/loyalty/preview-points`,
      {
        customerId: params.customerId,
        items: params.items.map((item) => ({
          variantId: item.variantId,
          quantity: item.quantity,
          lineSubtotal: item.lineSubtotal,
        })),
      },
      { timeoutMs: 10_000 },
    );
  },

  async getEligibleLoyaltyRewards(
    customerId: string,
    affordableOnly = true,
  ): Promise<{
    rewards: Array<{
      id: string;
      name: string;
      description: string | null;
      imageUrl: string | null;
      rewardType: string;
      pointsCost: number;
      fulfillment: unknown;
      sortOrder: string;
    }>;
    balance: number | null;
  }> {
    const url = new URL(`${config.apiUrl}/pos/loyalty/rewards`);
    url.searchParams.set("customerId", customerId);
    if (affordableOnly) {
      url.searchParams.set("affordableOnly", "true");
    }
    return fetcher.get(url.toString());
  },

  async previewLoyaltyReward(params: {
    customerId: string;
    rewardId: string;
    orderId?: string;
    cartItems?: Array<{
      variantId?: string | null;
      quantity?: number;
      lineSubtotal?: number;
    }>;
  }): Promise<{
    eligible: boolean;
    error?: string;
    pointsCost: number;
    currentBalance: number;
    balanceAfter: number;
    discountPreview?: number;
    rewardName: string;
    rewardType: string;
  }> {
    return fetcher.post(`${config.apiUrl}/pos/loyalty/preview-reward`, params);
  },
};
